import React from 'react';
import PropTypes from 'prop-types';
import api from 'api';
import Component from 'BaseComponent';
import { notice, alert } from 'ducks/notifications';
import { reduxForm } from 'redux-form';
import validators from 'utils/validators';
import generateFormValidation from 'utils/generateFormValidation';
import convertToFormErrors from 'utils/convertToFormErrors';
import { withRouter } from 'react-router-dom';
import Field from 'form/Field';
import Form from 'form/Form';
import { connect } from 'react-redux';
import SubmitButton from 'form/SubmitButton';
import autobind from 'autobind-decorator';
import { invalidateRequests } from 'redux-bees';
import formatBytes from 'bytes';
import Time from 'components/Time';
import OperationButton from 'components/OperationButton';
import Cf7Settings from 'components/Cf7Settings';
import DeploySettings from 'components/DeploySettings';
import CacheSettings from 'components/CacheSettings';
import ServerSettings from 'components/ServerSettings';
import Tooltip from 'components/Tooltip';

@withRouter

@connect((state, props) => ({
  initialValues: props.site.attributes,
}))

@reduxForm({
  form: 'Settings',
  validate: generateFormValidation({
    name: [validators.required()],
  }),
  onSubmit(values, dispatch, props) {
    const { site } = props;

    return dispatch(api.updateSite(
      { id: site.id },
      {
        data: {
          type: 'site',
          attributes: values,
        },
      },
    ))
    .then(() => {
      props.history.push(`/site/${site.id}/settings`);
      dispatch(notice('Settings.success'));
    })
    .catch((response) => {
      dispatch(alert('Settings.error'));
      return Promise.reject(convertToFormErrors(response));
    });
  },
})

export default class Settings extends Component {

  static propTypes = {
    handleSubmit: PropTypes.func.isRequired,
    submitting: PropTypes.bool.isRequired,
    dirty: PropTypes.bool.isRequired,
    error: PropTypes.string,
    dispatch: PropTypes.func.isRequired,
    match: PropTypes.object.isRequired,
    history: PropTypes.object.isRequired,
    site: PropTypes.object.isRequired,
  }

  @autobind
  handleDestroy() {
    const { dispatch, match, history } = this.props;
    if (confirm('Are you sure?')) {
      dispatch(api.destroySite({ id: match.params.siteId }))
      .then(() => dispatch(notice('Site.destroySuccessful')))
      .then(() => dispatch(invalidateRequests(api.getSites)))
      .then(() => history.push('/'))
      .catch(() => dispatch(alert('Site.destroyFailure')));
    }
  }

  @autobind
  handleReset() {
    const { dispatch, match } = this.props;
    if (confirm('Are you sure?')) {
      return dispatch(api.resetSite({ id: match.params.siteId }, null))
      .then(() => dispatch(notice('Site.resetSuccessful')))
      .catch(() => dispatch(alert('Site.resetFailure')));
    }
    return true;
  }

  @autobind
  handleClone() {
    const { dispatch, match, history } = this.props;
    if (confirm('Are you sure?')) {
      dispatch(api.cloneSite({ id: match.params.siteId }, null))
      .then(() => dispatch(notice('Site.cloneSuccessful')))
      .then(() => history.push('/'))
      .catch(() => dispatch(alert('Site.cloneFailure')));
    }
  }

  render() {
    const { site, handleSubmit, submitting, error, dirty } = this.props;

    return (
      <div>
        <div className="Site__area">
          <div className="Site__area__header">
            <div className="Site__area__header__title">
              Info
            </div>
          </div>
          <div className="Settings__area__items">
            {
              site.attributes.createdAt &&
              <div className="Settings__area__item">
                <div className="Settings__area__item__key">Site created</div>
                <Time value={site.attributes.createdAt} />
              </div>
            }
            <div className="Settings__area__item">
              <div className="Settings__area__item__key">
                Site slug
              </div>
              {site.attributes.slug}
            </div>
            {
              site.attributes.size > 0 &&
              <div className="Settings__area__item">
                <div className="Settings__area__item__key">
                  WordPress size
                </div>
                {formatBytes(site.attributes.size + 0, { unitSeparator: ' ' })}
              </div>
            }
            {
              site.attributes.deploySize > 0 &&
              <div className="Settings__area__item">
                <div className="Settings__area__item__key">
                  Static site size
                </div>
                {formatBytes(site.attributes.deploySize + 0, { unitSeparator: ' ' })}
              </div>
            }
            {
              site.attributes.startedAt &&
              <div className="Settings__area__item">
                <div className="Settings__area__item__key">
                  WordPress last started
                </div>
                <Time value={site.attributes.startedAt} />
              </div>
            }
            {
              site.attributes.pausedAt &&
              <div className="Settings__area__item">
                <div className="Settings__area__item__key">
                  WordPress last paused
                </div>
                <Time value={site.attributes.pausedAt} />
              </div>
            }
            {
              site.attributes.lastDeployAt &&
              <div className="Settings__area__item">
                <div className="Settings__area__item__key">
                  Last deploy
                </div>
                <Time value={site.attributes.lastDeployAt} />
              </div>
            }
            <div className="Settings__area__item">
              <div className="Settings__area__item__key">
                CF7 mails sent (last 30 days)
                {' '}
                <Tooltip label="Click for help">
                  <a href="https://www.hardypress.com/guides/contact-forms-on-hardypress/" target="_blank" rel="noopener noreferrer">
                    <i className="icon--question-circle" />
                  </a>
                </Tooltip>
              </div>
              {site.attributes.lastMonthFormSubmissionsCounter}
            </div>
          </div>
        </div>

        <div className="Site__area">
          <div className="Site__area__header">
            <div className="Site__area__header__title">
              Site settings
            </div>
          </div>
          <div className="Site__area__content">
            <Form error={error} onSubmit={handleSubmit}>
              <Field label="Settings.name" name="name">
                <input type="text" />
              </Field>
              <SubmitButton
                dirty={dirty}
                className="button button--secondary"
                submitting={submitting}
                label="Settings.submit"
              />
            </Form>
          </div>
        </div>
        <ServerSettings site={site} />

        <CacheSettings site={site} />

        <DeploySettings site={site} />

        <Cf7Settings site={site} />

        <div className="Site__area">
          <div className="Site__area__header">
            <div className="Site__area__header__title">
              Clone site
            </div>
          </div>
          <div className="Site__area__content">
            <p className="space--bottom-2">
              Create a copy of this site. Deploys and backups will NOT be duplicated.

            </p>
            {site.attributes.wpState !== 'paused' ?
              <Tooltip label="Switch WordPress OFF to clone this site">
                <button
                  className="button"
                  onClick={this.handleClone}
                  disabled
                >
                  Clone site
                </button>
              </Tooltip>
            :
              <button
                className="button"
                onClick={this.handleClone}
              >
                Clone site
              </button>
            }
          </div>
        </div>

        {
          site.attributes.adminInfo &&
            <div className="Site__area">
              <div className="Site__area__header">
                <div className="Site__area__header__title">
                  Panic mode
                </div>
              </div>
              <div className="Site__area__content">
                <p className="space--bottom-2">
                  We hope not, but it can happen that sometimes something goes wrong.
                  If your HardyPress dashboard goes in an undefined state,
                  stops responding or misbehaves,
                  press this button to reset your website to a previous working state.
                </p>
                <OperationButton
                  className="button button--alert"
                  onClick={this.handleReset}
                  label={['Stop and reset']}
                />
              </div>
            </div>
        }
        {site.attributes.wpState !== 'destroying' &&
          <div className="Site__area">
            <div className="Site__area__header">
              <div className="Site__area__header__title">
                Destroy site
              </div>
            </div>
            <div className="Site__area__content">
              <p className="space--bottom-2">
                This is irreversible. We will destroy your site and all
                associated backups. All data will be scrubbed and
                irretrievable.
              </p>
              <button
                className="button button--alert"
                onClick={this.handleDestroy}
              >
                Destroy
              </button>
            </div>
          </div>
        }
      </div>
    );
  }
}




// WEBPACK FOOTER //
// ./app/components/Settings.jsx